
import Vue from "vue";
import { Component, Inject, Prop } from "vue-property-decorator";
import {
  ButtonBarOrDropdownItem,
  Control,
  ControlConditions,
  DropdownOptions,
  PopulateOptions,
  PopulateWithFetch
} from "@/types/controls";
import { ElementStudioContext } from "./ElementStudioTabsAndPanels.vue";
import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";
import Repeater from "@/components/Reusable/CustomInputs/Repeater/Repeater.vue";
import ElementStudioConditionInput from "@/components/ElementStudio/ElementStudioConditionInput.vue";
import defensiveCopy from "@/util/defensive-copy";
import { getElementControlByElementControlPathWithPrefix } from "@/lib/elements/utils";
import Dropdown from "@/components/Reusable/Dropdown/Dropdown.vue";
import DropdownInput from "@/components/Reusable/CustomInputs/DropdownInput.vue";
import Icon from "@/components/UI/Icon.vue";
import ElementStudioIconsAutocomplete from "@/components/ElementStudio/ElementStudioIconsAutocomplete.vue";
import NiceButton from "@/components/UI/NiceButton.vue";

const DEFAULT_POPULATE = {
  path: "",
  text: "",
  value: "",
  condition: undefined,
  fetchDataAction: "",
  fetchContextPath: "",
  refetchPaths: []
};

@Component({
  components: {
    NiceButton,
    ElementStudioIconsAutocomplete,
    Icon,
    DropdownInput,
    Dropdown,
    ElementStudioConditionInput,
    Repeater,
    TextInput
  }
})
export default class ElementStudioDropdownOptions extends Vue {
  @Prop()
  value!: DropdownOptions;

  @Prop()
  control!: Control;

  @Prop()
  path!: string;

  @Inject("context") readonly context!: ElementStudioContext;

  get internalValue() {
    return this.value || {};
  }

  get repeaterPaths() {
    return this.context.getPathsToAllControls(["repeater"]);
  }

  get controlPaths() {
    return this.context.getPathsToAllControls();
  }

  get possibleValues() {
    const path = this.internalValue?.populate?.path;

    if (!path) return [];

    const control = this.getControl(path);

    if (!control) return [];

    const result = control.children.map(c => c.slug);

    if (!result) return [];

    return result;
  }

  getControl(path: string) {
    return getElementControlByElementControlPathWithPrefix(this.element, path);
  }

  get populate() {
    return this.internalValue.populate || DEFAULT_POPULATE;
  }

  get dropdownItems() {
    // @ts-ignore
    return this.control.options.items || [{ value: "value", text: "Label" }];
  }

  get element() {
    return this.context.element();
  }

  updateDropdownItems(newItems: ButtonBarOrDropdownItem[]) {
    this.$emit("updateDropdownItems", newItems);
  }

  updateDropdownItem(newItem: ButtonBarOrDropdownItem, index: number) {
    this.$emit("updateDropdownItem", { newItem, index });
  }

  updateEnablePopulate(enabled: boolean) {
    if (enabled) {
      this.emit({
        populate: DEFAULT_POPULATE
      });
    } else {
      this.emit(undefined);
    }
  }

  updateCondition(newCondition: ControlConditions) {
    const newPopulate = defensiveCopy(this.populate);

    if (Array.isArray(newCondition) && !newCondition.length) {
      delete newPopulate.condition;
    } else {
      newPopulate.condition = newCondition;
    }

    this.emit({
      populate: newPopulate
    });
  }

  updatePopulateValue(
    key:
      | keyof Omit<PopulateOptions, "condition">
      | keyof Omit<PopulateWithFetch, "refetchPaths">,
    value: string
  ) {
    const newPopulate = defensiveCopy(this.populate);

    newPopulate[key] = value;

    this.emit({
      populate: newPopulate
    });
  }

  updateRefetchPaths(newRefetchPaths: ({} | string)[]) {
    const newPopulate = defensiveCopy(this.populate);

    // Turn objects into string. It happens when Repeater adds a new item
    newPopulate.refetchPaths = newRefetchPaths.map(dependency =>
      typeof dependency === "string" ? dependency : ""
    );

    this.emit({
      populate: newPopulate
    });
  }

  updateRefetchPath(index: number, newValue: string) {
    const newPopulate = defensiveCopy(this.populate);

    if (!newPopulate.refetchPaths) {
      return;
    }

    newPopulate.refetchPaths[index] = newValue;

    this.emit({
      populate: newPopulate
    });
  }

  populateWithHtmlTagOptions() {
    if (!this.element.htmlTag.options) {
      return;
    }

    const items = this.element.htmlTag.options.map(option => ({
      text: option,
      value: option
    }));

    this.updateDropdownItems(items);
  }

  emit(dropdownOptions: DropdownOptions | undefined) {
    this.$emit("input", dropdownOptions);
  }
}
